import { useState } from "react";
import { LocalizedLink } from "@/i18n/LocalizedLink";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { imgUrl } from "@/lib/api";
import { useLang } from "@/i18n/LangContext";
import { useUserAuth } from "@/contexts/UserAuthContext";
import LoginRequiredModal from "@/components/LoginRequiredModal";
import { Key, ShieldCheck, ArrowLeft, Tag } from "lucide-react";

export default function GameDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const { user } = useUserAuth();
  const [loginOpen, setLoginOpen] = useState(false);
  const { data: product, isLoading } = useQuery({
    queryKey: ["product", id],
    queryFn: async () => (await api.get(`/products/${id}`)).data,
  });

  if (isLoading) return <div className="max-w-7xl mx-auto px-6 py-20 text-zinc-500">{t("common.loading")}</div>;
  if (!product) return <div className="max-w-7xl mx-auto px-6 py-20 text-zinc-500">{t("common.empty")}</div>;

  return (
    <main data-testid="game-detail-page">
      <div className="relative h-[42vh] md:h-[55vh] bg-cover bg-center" style={{ backgroundImage: product.image_url ? `url(${imgUrl(product.image_url)})` : "" }}>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-transparent" />
        <div className="absolute inset-0 max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-12">
          <LocalizedLink to="/games" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 text-sm" data-testid="back-to-games">
            <ArrowLeft className="w-4 h-4 rtl-flip" /> {t("nav.games")}
          </LocalizedLink>
          <div className="inline-flex items-center gap-2 mb-3">
            <span className="px-3 py-1 rounded text-xs font-bold uppercase tracking-wider bg-brand-cyan/15 text-brand-cyan border border-brand-cyan/30">
              <Tag className="w-3 h-3 inline me-1" /> {product.platform}
            </span>
          </div>
          <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight">{product.title}</h1>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <h2 className="font-heading text-xl font-semibold mb-3 text-zinc-300">About</h2>
          <p className="text-zinc-400 leading-relaxed text-lg">{product.description || "—"}</p>
        </div>

        <aside className="bg-[#0c0c0c] border border-white/5 rounded-xl p-6 h-fit">
          <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3">Platform</div>
          <div className="font-heading text-2xl font-bold mb-6">{product.platform}</div>
          {user ? (
            <LocalizedLink to="/verify" className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-5 py-3 rounded-lg transition-all glow-blue" data-testid="game-detail-redeem-cta">
              <Key className="w-4 h-4" />
              {t("game.claim")}
            </LocalizedLink>
          ) : (
            <button
              type="button"
              onClick={() => setLoginOpen(true)}
              className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-5 py-3 rounded-lg transition-all glow-blue"
              data-testid="game-detail-redeem-cta"
            >
              <Key className="w-4 h-4" />
              {t("game.claim")}
            </button>
          )}
          <div className="flex items-center gap-2 mt-4 text-xs text-zinc-500">
            <ShieldCheck className="w-4 h-4 text-brand-green" />
            AES-encrypted credentials
          </div>
        </aside>
      </div>
      <LoginRequiredModal open={loginOpen} onClose={() => setLoginOpen(false)} next="/verify" />
    </main>
  );
}
